import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
    TaxSummaryApiResponse,
    CapitalGainsApiResponse,
    CryptoTaxOptionsApiResponse,
    TaxBreakdownApiResponse,
    EstimateRequest,
    TaxCountry,
    UpcomingTaxEventsApiResponse,
} from '../models/tax-center.models';

@Injectable({ providedIn: 'root' })
export class TaxCenterApi {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.apiBaseUrl;

    getTaxSummary(
        taxYear: number,
        baseCurrency: string = 'USD',
        country?: TaxCountry | null,
    ): Observable<TaxSummaryApiResponse> {
        let params = new HttpParams()
            .set('tax_year', taxYear.toString())
            .set('base_currency', baseCurrency);
        // AU → backend reports on the Jul–Jun financial year; omitted → calendar year.
        if (country) params = params.set('country', country);
        return this.http.get<TaxSummaryApiResponse>(`${this.baseUrl}/transactions/tax-summary`, {
            params,
        });
    }

    getCapitalGains(
        taxYear: number,
        baseCurrency: string = 'USD',
        country?: TaxCountry | null,
        page: number = 1,
        limit: number = 10,
        term: 'all' | 'short' | 'long' = 'all',
    ): Observable<CapitalGainsApiResponse> {
        let params = new HttpParams()
            .set('tax_year', taxYear.toString())
            .set('base_currency', baseCurrency)
            .set('page', page.toString())
            .set('limit', limit.toString())
            .set('term', term);
        if (country) params = params.set('country', country);
        return this.http.get<CapitalGainsApiResponse>(`${this.baseUrl}/transactions/capital-gains`, {
            params,
        });
    }

    downloadForm8949Pdf(taxYear: number, country?: TaxCountry | null): Observable<Blob> {
        let params = new HttpParams().set('tax_year', taxYear.toString());
        // A user who moved picks which residence's report; the backend only honours their own.
        if (country) params = params.set('country', country);
        return this.http.get(`${this.baseUrl}/transactions/form-8949`, {
            params,
            responseType: 'blob',
        });
    }

    // DE (Anlage SO) / AU (myTax CGT) capital-gains summary PDF. Country is resolved
    // server-side from the user's profile; the param only sets the AU Jul–Jun period.
    downloadTaxSummaryPdf(taxYear: number, country?: TaxCountry | null): Observable<Blob> {
        let params = new HttpParams().set('tax_year', taxYear.toString());
        if (country) params = params.set('country', country);
        return this.http.get(`${this.baseUrl}/transactions/tax-summary-pdf`, {
            params,
            responseType: 'blob',
        });
    }

    // Per-disposal ITEMIZED filing PDF (Form 8949-style table; India Schedule VDA today).
    // Country is resolved server-side from the user's profile.
    downloadDisposalTablePdf(taxYear: number, country?: TaxCountry | null): Observable<Blob> {
        let params = new HttpParams().set('tax_year', taxYear.toString());
        if (country) params = params.set('country', country);
        return this.http.get(`${this.baseUrl}/transactions/disposal-report-pdf`, {
            params,
            responseType: 'blob',
        });
    }

    // Per-disposal capital-gains report (CSV) in the user's tax-country currency (EUR/AUD).
    downloadTaxReportCsv(taxYear: number, country?: TaxCountry | null): Observable<Blob> {
        let params = new HttpParams().set('tax_year', taxYear.toString());
        if (country) params = params.set('country', country);
        return this.http.get(`${this.baseUrl}/transactions/tax-report-csv`, {
            params,
            responseType: 'blob',
        });
    }

    // India only: each declared §56(2)(x) gift with its sender / tx hash, plus the Schedule OS totals.
    downloadGiftReportCsv(taxYear: number): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/transactions/gift-report-csv`, {
            params: new HttpParams().set('tax_year', taxYear.toString()),
            responseType: 'blob',
        });
    }

    // India only: §194S TDS per trade beside 1% of the VDA transferred, with quarterly totals for 26AS.
    // India only: Schedule OS worksheet — crypto income (staking, lending, airdrops) and gifts.
    downloadScheduleOsPdf(taxYear: number): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/transactions/schedule-os-pdf`, {
            params: new HttpParams().set('tax_year', taxYear.toString()),
            responseType: 'blob',
        });
    }

    downloadTdsReportCsv(taxYear: number): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/transactions/tds-report-csv`, {
            params: new HttpParams().set('tax_year', taxYear.toString()),
            responseType: 'blob',
        });
    }

    recalculate(): Observable<any> {
        return this.http.post(`${this.baseUrl}/transactions/recalculate`, {});
    }

    getCryptoTaxOptions(): Observable<CryptoTaxOptionsApiResponse> {
        return this.http.get<CryptoTaxOptionsApiResponse>(`${this.baseUrl}/tax/crypto/options`);
    }

    estimateCryptoTax(body: EstimateRequest): Observable<TaxBreakdownApiResponse> {
        return this.http.post<TaxBreakdownApiResponse>(`${this.baseUrl}/tax/crypto/estimate`, body);
    }

    getUpcomingTaxEvents(): Observable<UpcomingTaxEventsApiResponse> {
        return this.http.get<UpcomingTaxEventsApiResponse>(
            `${this.baseUrl}/tax/crypto/upcoming-events`,
        );
    }
}
